// Tech purchase orders: every read and write of /poRequests (rules and shapes in partRequestsCore.js),
// shared by the techs app (partRequestsTech.js) and the office's Inbox › Part requests (partRequests.js).
// A request is numbered the moment it's opened (the same counter as every PO), so it needs a connection.
// Changes send only the fields that changed. Each step also posts an event in the work order's
// Ask the office thread, so the conversation and the order sit together.

import { ref, onValue, query, orderByChild, equalTo, limitToLast, update, push, runTransaction } from 'firebase/database';
import { database } from './initFirebase';
import { nextPurchaseOrderNumber, generateId } from './utilities';
import * as C from './partRequestsCore';
import * as T from './threadCore';
import { postMessage } from './threadData';

const reqRef = id => ref(database, `poRequests/${id}`);

/** Open a new numbered PO for this op (a draft only the tech sees). Returns { id, req }. */
export async function createRequest({ uid, name, op }) {
  const id = push(ref(database, 'poRequests')).key;
  const poRef = await nextPurchaseOrderNumber();
  const req = C.newRequest({ ref: poRef, uid, name, op });
  await update(reqRef(id), req);
  return { id, req };
}

/** Save the fields this person changed. */
export const saveFields = (id, fields) => update(reqRef(id), fields);

const threadInfo = req => ({ workOrder: req.Work_Order, boatName: req.Boat_Name || '', customerName: req.Customer_Name || '', customerId: req.Customer_ID || '' });

function postEvent(req, { uid, name, role, text }) {
  if (!req?.Work_Order) return;
  try {
    const msg = T.newMessage({ uid, name, role, text, opId: req.Op_Id || '', opTitle: req.Op_Title || '', kind: 'event' });
    postMessage(req.Work_Order, msg, { info: threadInfo(req) }).saved.catch(err => console.warn('PO: thread note not saved', err));
  } catch (err) { console.warn('PO: thread note not made', err); }
}

/** The tech sends it to the office. */
export async function sendRequest(id, req, { uid, name }) {
  const fields = { Status: 'waiting', Sent_At: Date.now(), Lines: C.keptLines(req.Lines) };
  await update(reqRef(id), fields);
  postEvent({ ...req, ...fields }, { uid, name, role: 'tech', text: C.eventText('sent', { ...req, ...fields }) });
}

/** The tech takes it back (a draft or one still waiting). The number stays used, marked cancelled. */
export const cancelRequest = id => update(reqRef(id), { Status: 'cancelled', Cancelled_At: Date.now() });

/** Off the tech's Today card. */
export const clearRequest = id => update(reqRef(id), { Tech_Cleared: true });

/** Claim a waiting request for this decision (a transaction, so two people can't both act). Returns the request. */
async function claim(id, status) {
  let seen = null;
  const res = await runTransaction(ref(database, `poRequests/${id}`), (cur) => {
    seen = cur;
    if (cur === null) return null; // not in this page's copy yet: the server runs it again with the real one
    if (cur.Status !== 'waiting') return undefined; // abort
    return { ...cur, Status: status };
  }, { applyLocally: false });
  const got = res.snapshot.val();
  if (!res.committed || !got || got.Status !== status) throw new Error(seen ? `Already ${C.statusOf(seen).text.toLowerCase()}` : 'This request was withdrawn or deleted');
  return got;
}

/**
 * The office approves: claims it, then writes the /inventoryPOs record (same id and number), any new
 * /inventory parts (addInv: { keptLineIndex: true } for lines not stocked yet) and the request's details
 * in one update. eta: the expected delivery ('YYYY-MM-DD' or ''). If the write fails the request goes
 * back to waiting. Returns the PO.
 */
export async function approveRequest(id, req, { lines, prices, parts, user, vendor, eta = '', addInv = {}, today }) {
  const cur = await claim(id, 'ordered');
  const now = Date.now();
  const r = { ...cur, Vendor_Id: vendor?.id || cur.Vendor_Id || '', Vendor_Name: vendor?.Vendor_Name || cur.Vendor_Name };
  const placedLabel = new Date(now).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const kept = C.keptLines(lines || cur.Lines);
  const day = today || new Date().toLocaleDateString('en-CA');
  const newParts = [];
  for (let i = 0; i < kept.length; i++) {
    if (kept[i].partId || !addInv[i]) continue;
    const part = C.newPartRecord(kept[i], { id: generateId(), vendorName: r.Vendor_Name, priceCents: prices?.[i], today: day, who: user.name || '', ref: r.Ref, wo: r.Work_Order });
    kept[i] = { ...kept[i], partId: part.id };
    newParts.push(part);
  }
  const po = C.poFromRequest(r, { id, lines: kept, prices, parts: [...(parts || []), ...newParts], user, placedLabel, now, eta });
  const fields = {
    [`inventoryPOs/${id}`]: po,
    [`poRequests/${id}/Po_Id`]: id,
    [`poRequests/${id}/Lines`]: kept,
    [`poRequests/${id}/Vendor_Id`]: r.Vendor_Id || null,
    [`poRequests/${id}/Vendor_Name`]: r.Vendor_Name,
    [`poRequests/${id}/Decided_By`]: user.name || '',
    [`poRequests/${id}/Decided_At`]: now,
    [`poRequests/${id}/Expected_Date`]: C.isDay(eta) ? eta : null,
    [`poRequests/${id}/Tech_Cleared`]: null,
  };
  newParts.forEach((p) => { fields[`inventory/${p.id}`] = p; });
  try {
    await update(ref(database), fields);
  } catch (err) {
    await update(ref(database, `poRequests/${id}`), { Status: 'waiting' }).catch(() => {});
    throw err;
  }
  const done = { ...r, Lines: kept, Expected_Date: C.isDay(eta) ? eta : '' };
  postEvent(done, { uid: user.uid, name: user.name, role: 'office', text: C.eventText('ordered', done) });
  return { po, newParts };
}

/**
 * The expected delivery changed (the vendor confirmed, or it slipped): every line still due on the PO.
 * functions/poRequests.js carries it to the request, the tech and the work order's thread.
 */
export const setExpected = (id, po, day) => update(ref(database, `inventoryPOs/${id}`), C.etaFields(po, day));

export async function declineRequest(id, req, { reason, user }) {
  const why = String(reason || '').trim().slice(0, 300);
  const cur = await claim(id, 'declined');
  await update(ref(database, `poRequests/${id}`), { Decline_Reason: why, Decided_By: user.name || '', Decided_At: Date.now(), Tech_Cleared: null });
  postEvent(cur, { uid: user.uid, name: user.name, role: 'office', text: C.eventText('declined', cur, why) });
}

/** The PO an approved request became (for its receiving progress). */
export const watchPo = (id, cb) => onValue(ref(database, `inventoryPOs/${id}`), s => cb(s.val()), () => cb(null));

/** One request, live. */
export const watchRequest = (id, cb) => onValue(reqRef(id), s => cb(s.val()), err => { console.warn('PO unavailable', err); cb(null); });

/** This tech's requests (the database rules only let a tech read their own, by this query). */
export const watchMine = (uid, cb) => onValue(query(ref(database, 'poRequests'), orderByChild('By_Uid'), equalTo(uid)), s => cb(s.val() || {}), (err) => { console.warn('My POs unavailable', err); cb({}); });

/** The office: one status, or the latest 150 of any. */
export const watchOffice = (filter, cb) => onValue(
  filter === 'all' ? query(ref(database, 'poRequests'), orderByChild('Created_At'), limitToLast(150)) : query(ref(database, 'poRequests'), orderByChild('Status'), equalTo(filter)),
  s => cb(s.val() || {}), (err) => { console.warn('Part requests unavailable', err); cb({}); });

/** The rail count: waiting on the office. */
export const watchWaiting = cb => onValue(query(ref(database, 'poRequests'), orderByChild('Status'), equalTo('waiting')), s => cb(Object.keys(s.val() || {}).length), () => cb(0));

/** The vendor directory (small: Settings › Vendors). */
export const watchVendors = cb => onValue(ref(database, 'settings/vendorList'), s => cb(s.val() || {}), () => cb({}));
